import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { SUPPORTED_LANGUAGES } from '../i18n';

const API = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { user, token } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationCache, setTranslationCache] = useState({});

  // Sync with i18n language
  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  // Load user's preferred language from profile
  useEffect(() => {
    if (user?.preferred_language && user.preferred_language !== currentLanguage) {
      changeLanguage(user.preferred_language);
    }
  }, [user]);

  const changeLanguage = async (langCode) => {
    if (!SUPPORTED_LANGUAGES.find(l => l.code === langCode)) {
      console.error('Unsupported language:', langCode);
      return;
    }

    // Change i18n language (for static UI strings)
    await i18n.changeLanguage(langCode);
    setCurrentLanguage(langCode);
    
    // Save to localStorage
    localStorage.setItem('vbb_language', langCode);

    // Update user preference if logged in
    if (token && user) {
      try {
        await fetch(`${API}/api/users/me`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ preferred_language: langCode })
        });
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  // Translate dynamic content using AI
  const translateContent = async (text, contentType = 'general') => {
    if (!text || currentLanguage === 'en') return text;
    
    // Check local cache first
    const cacheKey = `${text.substring(0, 50)}_${currentLanguage}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }

    setIsTranslating(true);
    try {
      const response = await fetch(`${API}/api/translate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          target_language: currentLanguage,
          content_type: contentType
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Cache the translation
        setTranslationCache(prev => ({
          ...prev,
          [cacheKey]: data.translated_text
        }));
        return data.translated_text;
      }
      return text; // Return original on error
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    } finally {
      setIsTranslating(false);
    }
  };

  // Batch translate multiple items
  const translateBatch = async (items, contentType = 'general') => {
    if (!items?.length || currentLanguage === 'en') return items;

    setIsTranslating(true);
    try {
      const response = await fetch(`${API}/api/translate/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(item => ({
            id: item.id,
            text: item.text || item.content || item.title || ''
          })),
          target_language: currentLanguage,
          content_type: contentType
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Merge translations back into items
        return items.map(item => {
          const translation = data.results.find(r => r.id === item.id);
          return translation 
            ? { ...item, translatedText: translation.translated_text }
            : item;
        });
      }
      return items;
    } catch (error) {
      console.error('Batch translation error:', error);
      return items;
    } finally {
      setIsTranslating(false);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    translateContent,
    translateBatch,
    isTranslating,
    supportedLanguages: SUPPORTED_LANGUAGES,
    getLanguageInfo: (code) => SUPPORTED_LANGUAGES.find(l => l.code === code)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
